import { useState, useEffect, useCallback, useRef } from 'react';
import { lockScroll, unlockScroll } from '../smoothScroll';
import './gallery.css';

const IMAGES = [
    { src: "/gallery/9.webp",   alt: "Hair style 9"  },
    { src: "/gallery/10.webp",  alt: "Hair style 10" },
    { src: "/gallery/11.webp",  alt: "Hair style 11" },
    { src: "/gallery/12.webp",  alt: "Hair style 12" },
    { src: "/gallery/13.webp",  alt: "Hair style 13" },
    { src: "/gallery/14.webp",  alt: "Hair style 14" },
    { src: "/gallery/15.webp",  alt: "Hair style 15" },
    { src: "/gallery/1.webp",  alt: "Hair style 1"  },
    { src: "/gallery/2.webp",  alt: "Hair style 2"  },
    { src: "/gallery/3.webp",  alt: "Hair style 3"  },
    { src: "/gallery/4.webp",  alt: "Hair style 4"  },
    { src: "/gallery/5.webp",  alt: "Hair style 5"  },
    { src: "/gallery/6.webp",  alt: "Hair style 6"  },
    { src: "/gallery/7.webp",  alt: "Hair style 7"  },
    { src: "/gallery/8.webp",  alt: "Hair style 8"  },

];

const CLOSE_MS = 240;       // keep in sync with .is-closing in gallery.css
const SETTLE_MS = 280;      // fly-out / snap-back after a swipe is released
const DRAG_START_PX = 8;    // movement before a touch counts as a swipe, not a tap
const SETTLE_EASE = 'cubic-bezier(0.22, 0.8, 0.3, 1)';

const wrap = (i) => (i + IMAGES.length) % IMAGES.length;

// x: how far the top photo is dragged; peek: which neighbour shows underneath (+1 next, -1 prev)
// phase: idle | dragging (follows the finger) | settling (snapping back) | flying (swiped away)
const REST = { x: 0, peek: 0, phase: 'idle' };

export default function Gallery() {
    const [active, setActive] = useState(null);
    const [closing, setClosing] = useState(false);
    const [drag, setDrag] = useState(REST);
    const [fade, setFade] = useState(false);

    const gesture = useRef(null);
    const closeTimer = useRef(null);
    const flyTimer = useRef(null);
    const swallowClick = useRef(false);

    const isOpen = active !== null;

    const open = useCallback((i) => {
        clearTimeout(closeTimer.current);
        setClosing(false);
        setFade(false);
        setDrag(REST);
        setActive(i);
    }, []);

    const close = useCallback(() => {
        clearTimeout(closeTimer.current);
        setClosing(true);
        closeTimer.current = setTimeout(() => {
            setActive(null);
            setClosing(false);
            setDrag(REST);
        }, CLOSE_MS);
    }, []);

    // Arrow buttons / keyboard
    const step = useCallback((dir) => {
        clearTimeout(flyTimer.current);
        setFade(true);
        setDrag(REST);
        setActive(i => (i === null ? i : wrap(i + dir)));
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape')      close();
            if (e.key === 'ArrowLeft')   step(-1);
            if (e.key === 'ArrowRight')  step(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, close, step]);

    // Freeze the page behind the lightbox (stays frozen through the close animation)
    useEffect(() => {
        if (!isOpen) return;
        lockScroll();
        return unlockScroll;
    }, [isOpen]);

    // Warm the neighbours so the photo underneath is ready the moment a swipe starts
    useEffect(() => {
        if (active === null) return;
        [1, -1].forEach(dir => { new Image().src = IMAGES[wrap(active + dir)].src; });
    }, [active]);

    useEffect(() => () => {
        clearTimeout(closeTimer.current);
        clearTimeout(flyTimer.current);
    }, []);

    // ── Swipe: the photo follows the finger, then flies off or snaps back ──
    const onPointerDown = (e) => {
        if (closing || drag.phase === 'flying') return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('button')) return;
        gesture.current = {
            id: e.pointerId,
            startX: e.clientX,
            lastX: e.clientX,
            lastT: e.timeStamp,
            velocity: 0,
            dragging: false,
        };
    };

    const onPointerMove = (e) => {
        const g = gesture.current;
        if (!g || g.id !== e.pointerId) return;
        const dx = e.clientX - g.startX;
        if (!g.dragging) {
            if (Math.abs(dx) < DRAG_START_PX) return;
            g.dragging = true;
            // Keep receiving moves if the finger leaves the window; not fatal if refused
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
        }
        const dt = e.timeStamp - g.lastT;
        if (dt > 0) g.velocity = (e.clientX - g.lastX) / dt;
        g.lastX = e.clientX;
        g.lastT = e.timeStamp;
        setDrag({ x: dx, peek: dx < 0 ? 1 : -1, phase: 'dragging' });
    };

    const onPointerEnd = (e) => {
        const g = gesture.current;
        if (!g || g.id !== e.pointerId) return;
        gesture.current = null;
        if (!g.dragging) return;

        // The click that follows a drag must not close the lightbox
        swallowClick.current = true;
        setTimeout(() => { swallowClick.current = false; }, 250);

        const dx = g.lastX - g.startX;
        const width = window.innerWidth;
        const far = Math.abs(dx) > Math.min(140, width * 0.28);
        const flick = Math.abs(g.velocity) > 0.45 && Math.abs(dx) > 30 && Math.sign(g.velocity) === Math.sign(dx);

        if (e.type === 'pointercancel' || !(far || flick)) {
            setDrag(d => ({ ...d, x: 0, phase: 'settling' }));
            return;
        }

        const dir = dx < 0 ? 1 : -1;
        setDrag({ x: -dir * width, peek: dir, phase: 'flying' });
        flyTimer.current = setTimeout(() => {
            setFade(false);
            setActive(i => (i === null ? i : wrap(i + dir)));
            setDrag(REST);
        }, SETTLE_MS);
    };

    const onBackdropClick = () => {
        if (swallowClick.current) return;
        close();
    };

    // How far the swipe has progressed, 0 → 1, drives the photo underneath
    const width = typeof window !== 'undefined' ? window.innerWidth : 1;
    const progress = Math.min(Math.abs(drag.x) / (width * 0.45), 1);
    const settle = drag.phase === 'dragging'
        ? 'none'
        : `transform ${SETTLE_MS}ms ${SETTLE_EASE}, opacity ${SETTLE_MS}ms ease`;
    const under = isOpen && drag.peek !== 0 ? wrap(active + drag.peek) : null;

    return (
        <>
            <section className="gallery" id="gallery">

                {/* ── Header ── */}
                <div className="gallery-header" data-aos="fade-up">
                    <div className="gallery-header-left">
                        <div className="gallery-eyebrow">
                            <span className="gallery-eyebrow-dot" />
                            <span>Gallery</span>
                        </div>
                        <h2 className="gallery-title">
                            The <em>Work</em>
                        </h2>
                    </div>
                    <div className="gallery-header-right">
                        <p className="gallery-header-note">Selected works</p>
                        <p className="gallery-header-count">
                            <em>{String(IMAGES.length).padStart(2, '0')}</em> photographs
                        </p>
                    </div>
                </div>

                <div className="gallery-full-rule" data-aos="fade-up" />

                {/* ── Editorial Grid ── */}
                <div className="gallery-grid" data-aos="fade-up" data-aos-delay="80">
                    {IMAGES.map((img, i) => (
                        <button
                            key={i}
                            className="gallery-item"
                            onClick={e => { e.currentTarget.blur(); open(i); }}
                            aria-label={`Open ${img.alt}`}
                        >
                            <img src={img.src} alt={img.alt} loading="lazy" />
                            <div className="gallery-item-overlay">
                                <span className="gallery-item-num">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <svg
                                    className="gallery-item-icon"
                                    width="13" height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                    aria-hidden="true"
                                >
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                </svg>
                            </div>
                        </button>
                    ))}
                </div>

            </section>

            {/* ── Lightbox ── */}
            {isOpen && (
                <div
                    className={`gallery-lightbox${closing ? ' is-closing' : ''}`}
                    onClick={onBackdropClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerEnd}
                    onPointerCancel={onPointerEnd}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image viewer"
                >
                    {/* Close */}
                    <button className="lb-close" onClick={e => { e.stopPropagation(); close(); }} aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Counter */}
                    <div className="lb-counter" aria-live="polite">
                        <span className="lb-counter-current">{String(active + 1).padStart(2, '0')}</span>
                        <span className="lb-counter-sep" />
                        <span className="lb-counter-total">{String(IMAGES.length).padStart(2, '0')}</span>
                    </div>

                    {/* Prev */}
                    <button
                        className="lb-nav lb-prev"
                        onClick={e => { e.stopPropagation(); step(-1); }}
                        aria-label="Previous image"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
                            <path d="M19 12H5M12 5l-7 7 7 7" />
                        </svg>
                    </button>

                    {/* Next */}
                    <button
                        className="lb-nav lb-next"
                        onClick={e => { e.stopPropagation(); step(1); }}
                        aria-label="Next image"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Image stack — the neighbour waits underneath and grows in as the top photo is dragged away */}
                    <div
                        className="lb-image-wrap"
                        onClick={e => e.stopPropagation()}
                    >
                        {under !== null && (
                            <img
                                key={`under-${under}`}
                                src={IMAGES[under].src}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                className="lb-image lb-image-under"
                                style={{
                                    transform: `scale(${0.9 + 0.1 * progress})`,
                                    opacity: progress,
                                    transition: settle,
                                }}
                            />
                        )}
                        <img
                            key={active}
                            src={IMAGES[active].src}
                            alt={IMAGES[active].alt}
                            draggable={false}
                            className={`lb-image${fade ? ' is-fading' : ''}`}
                            style={{
                                transform: `translate3d(${drag.x}px, 0, 0) rotate(${(drag.x / width) * 14}deg)`,
                                transition: settle,
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
